import React, { useState } from 'react';
import aOrAn from "indefinite";

// Material UI
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Divider from '@material-ui/core/Divider';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import DescriptionIcon from '@material-ui/icons/Description';
import GroupAddIcon from '@material-ui/icons/GroupAdd';
import AddItemIcon from '@material-ui/icons/PostAdd';
import Typography from '@material-ui/core/Typography';
import SendIcon from '@material-ui/icons/Send';

// Components
import DashboardLayout from '../_layout/DashboardLayout/dashboard.layout';
import { PageTitle, SectionHeading, Text } from '../../_core/_ui/typography.component';
import { PageHeaderRow, SectionWrapper, Spacer } from '../../_core/_ui/structure.components';
import { PlayerListData } from '../../Player/PlayerList/player-list.graphql';
import { CreatePlayerData } from '../../Player/CreatePlayer/create-player.graphql';
import { CardButton, CardButtonIcon, PrimaryButton, SecondaryButton } from '../../_core/_ui/buttons.component';
import { ImportPlayersGraphQL } from '../../Player/ImportPlayers/import-players.graphql';
import { ImportProgressNotice } from '../../Player/ImportPlayers/import-progress-notice.component';
import { CompanyPlayerListGraphQL } from '../../Player/CompanyPlayerList/company-player-list.graphql';
import { buildSendEmails, getCountHasUnsentType } from '../../../lib/services/email-log.service';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';
import { SportTerminology } from '../../../lib/types';
import { pluralTerm } from '../../../lib/helpers/term.helper';
import { pluralize } from '../../../lib/helpers/text.helpers';
import {
  describeInaccessibleAgeGroups,
  readInaccessibleAgeGroupIds
} from '../../../lib/helpers/age-group.helpers';
import { useIntlSnackbar } from '../../../lib/hooks/use-intl-snackbar';
import { messages } from './manage-players.messages';
import Alert from '@material-ui/lab/Alert';
import { ROLES } from '../../../../_configuration';

export const ManagePlayersPage = () => {
  const [sending, setSending] = useState(false);
  const [activeForm, setActiveForm] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<React.ReactNode>(null)
  // Held here, not in the import panel: the panel closes the moment the rows are submitted.
  const [importingRowCount, setImportingRowCount] = useState<number | null>(null)
  const { count, loading, error: countError, ageGroupIds, refetch } = getCountHasUnsentType('WELCOME');
  const { sendEmails } = buildSendEmails();
  const termLookup = useCompanySportTerminology()
  const showSnackbar = useIntlSnackbar();

  const athleteTerm = termLookup[SportTerminology.ATHLETE]
  const athletesTerm = pluralTerm(athleteTerm)
  const HEADERS: any = {
    create: 'Create ' + aOrAn(athleteTerm),
    import: 'Import ' + athletesTerm,
    existing: 'Add Existing ' + athletesTerm
  };

  const FORM: any = {
    create: <CreatePlayerData onComplete={() => setActiveForm(null)} />,
    import: <ImportPlayersGraphQL
      onComplete={() => setActiveForm(null)}
      setSubmitError={setSubmitError}
      setImportingRowCount={setImportingRowCount} />,
    existing: <CompanyPlayerListGraphQL />
  }

  // A send refuses unless every age group in the saved filter is still reachable, and names the
  // ones it rejected — so a stale filter says which part is stale instead of "please try again".
  const handleSendWelcomeError = (error: any) => {
    console.error('sendEmails WELCOME failed:', error);

    if (error?.message?.includes('communication is already in progress')) {
      return showSnackbar(messages.errWelcomeInProgress, 'warning');
    }

    const inaccessibleIds = readInaccessibleAgeGroupIds(error);
    if (!inaccessibleIds.length) return showSnackbar(messages.errWelcomeFailed);

    return showSnackbar(messages.errWelcomeInaccessibleAgeGroups, 'error', undefined, {
      names: describeInaccessibleAgeGroups(inaccessibleIds),
      verb: pluralize(inaccessibleIds.length, 'is', 'are')
    });
  };

  const handleSendWelcomeEmails = () => {
    if (window.confirm('Are you sure you want to send welcome emails?')) {
      setSending(true);

      return sendEmails('WELCOME', ageGroupIds)
        .then(() => refetch())
        .then(() => showSnackbar(messages.snackWelcomeSent, 'success'))
        .catch(handleSendWelcomeError)
        .finally(() => setSending(false));
    }
  }

  return (
    <DashboardLayout requiredRoles={[ROLES.CONFIGURATION, ROLES.REPORTS]}>
      <PageHeaderRow><PageTitle>Manage {athletesTerm}</PageTitle></PageHeaderRow>
      <Grid container spacing={2}>
        {(!!countError || (count !== null && count > 0) || loading) && (<Grid item xs={12}>
          <Box display="flex" alignItems="center">
            {loading && <Typography>Checking whether any {athletesTerm.toLowerCase()} need to receive a welcome email...</Typography>}
            {!loading && !countError && count !== null && count > 0 && <Typography>Send welcome emails to {count} {athletesTerm.toLowerCase()} that have not received one</Typography>}
            {!loading && !!countError && (
              <Typography color="error">
                Couldn&apos;t check whether any {athletesTerm.toLowerCase()} need a welcome email. Refresh to try again.
              </Typography>
            )}
            <Spacer />
            <PrimaryButton disabled={loading || !ageGroupIds?.length || sending || !!countError} endIcon={<SendIcon />} onClick={() => handleSendWelcomeEmails()}>Send Welcome Emails</PrimaryButton>
          </Box>
        </Grid>)}
        <Grid item xs={12}>
          <SectionWrapper>
            {!!importingRowCount && <ImportProgressNotice rowCount={importingRowCount} />}
            <PlayerListData />
            {!!submitError && (
              <Box mt={2}>
                <Alert severity="error" onClose={() => setSubmitError(null)}>{submitError}</Alert>
              </Box>
            )}
          </SectionWrapper>
        </Grid>
        <Grid item xs={12}>
          <SectionWrapper>
            {!activeForm && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <CardButton onClick={() => setActiveForm('import')}>
                    <CardButtonIcon><DescriptionIcon color="primary" /></CardButtonIcon>
                    <SectionHeading>Import {athleteTerm} Data</SectionHeading>
                    <Text>(CSV)</Text>
                  </CardButton>
                </Grid>
                <Grid item xs={12} md={4}>
                  <CardButton onClick={() => setActiveForm('create')}>
                    <CardButtonIcon><AddItemIcon color="primary" /></CardButtonIcon>
                    <SectionHeading>Create {aOrAn(athleteTerm)}</SectionHeading>
                    <Text>(Manual Entry)</Text>
                  </CardButton>
                </Grid>
                <Grid item xs={12} md={4}>
                  <CardButton onClick={() => setActiveForm('existing')}>
                    <CardButtonIcon><GroupAddIcon color="primary" /></CardButtonIcon>
                    <SectionHeading>Add Existing {athletesTerm}</SectionHeading>
                    <Text>(From Another Assessment)</Text>
                  </CardButton>
                </Grid>
              </Grid>
            )}
            {activeForm && (<>
              <Box mb={2} display="flex" alignItems="center">
                <SectionHeading style={{ margin: 0 }}>{HEADERS[activeForm]}</SectionHeading>
                <Spacer />
                <SecondaryButton
                  onClick={() => setActiveForm(null)}
                  startIcon={<ArrowBackIosIcon />}
                >Back</SecondaryButton>
              </Box>
              <Divider />
              {FORM[activeForm]}
            </>)}
          </SectionWrapper>
        </Grid>
      </Grid>
    </DashboardLayout>
  );
};
